/**
 * useRegulaEvents — mounts the Regula locator engine (`regulaLocator.ts`) into a host element
 * while the flow is in its "capturing" state, and reports the capture back via callbacks.
 *
 * Analogous to useVeriffEvents (Veriff), but instead of `createVeriffFrame` it drives the
 * client-side Locate/BoundsLite capture from `regulaLocator.ts`. The heavy Regula SDK is
 * dynamically imported so it stays code-split out of the main bundle.
 */
import { useEffect } from "react";
import type { RefObject } from "react";
import type { RegulaLocatorResult } from "../../sdk/regulaLocator";

interface UseRegulaEventsParams {
  /** The element the Regula <document-reader> mounts into (rendered during "capturing"). */
  hostRef: RefObject<HTMLDivElement | null>;
  /** True only while the flow is in its capturing state. */
  active: boolean;
  /** Locale for the Regula SDK's own UI, already mapped via `toRegulaLocale`. */
  locale?: string;
  /** Translation dictionary for the Regula component UI, keyed by locale (matches `locale`). */
  translations?: Record<string, unknown>;
  onResult: (result: RegulaLocatorResult) => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
  /** Fired once the Regula SDK has loaded and its <document-reader> is mounted (launch resolved). */
  onReady?: () => void;
  /** Forwarded to launchRegulaLocator — see regulaLocator.ts for semantics. */
  onCameraStarted?: () => void;
  onSwitchCamera?: () => void;
}

export function useRegulaEvents({ hostRef, active, locale, translations, onResult, onClose, onError, onReady, onCameraStarted, onSwitchCamera }: UseRegulaEventsParams) {
  useEffect(() => {
    if (!active || !hostRef.current) return;
    const host = hostRef.current;

    let cleanup: (() => void) | undefined;
    let disposed = false;

    // Launch only on entering "capturing". Dynamic import keeps the Regula SDK code-split.
    import("../../sdk/regulaLocator")
      .then(({ launchRegulaLocator }) => launchRegulaLocator({ host, locale, translations, onResult, onClose, onError, onCameraStarted, onSwitchCamera }))
      .then((fn) => {
        if (disposed) {
          fn();
        } else {
          cleanup = fn;
          onReady?.();
        }
      })
      .catch((err) => onError?.(err instanceof Error ? err : new Error(String(err))));

    // Tear down when leaving "capturing" (or unmount). This effect doesn't launch at mount
    // (active starts false), so React StrictMode's mount double-invoke can't race the SDK global.
    return () => {
      disposed = true;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
